/**
 * Navbar — sticky top bar for the public marketing site.
 *
 * Reads brand + nav from useSiteConfig().  Responsive: collapses links into
 * a hamburger on narrow viewports.
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { COLOR, FONT, RADIUS, ELEVATION } from '../../../components/ss/tokens';
import useSiteConfig from '../useSiteConfig';
import BenevitaLogo from '../../../components/BenevitaLogo';

function isExternal(href) {
  return /^(https?:|mailto:|tel:)/i.test(href || '');
}

function NavAnchor({ link, style, onClick }) {
  // Anchors inside a page (e.g. /home#pricing) are best left as <a> so the
  // browser handles smooth-scroll natively.  Internal route changes use Link.
  if (!link?.href) return null;
  if (isExternal(link.href) || link.href.includes('#')) {
    return (
      <a href={link.href} onClick={onClick} style={style}>
        {link.label}
      </a>
    );
  }
  return (
    <Link to={link.href} onClick={onClick} style={style}>
      {link.label}
    </Link>
  );
}

export default function Navbar() {
  const { config } = useSiteConfig();
  const { brand, nav, theme } = config;
  const ui = config.ui || {};
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const linkStyle = {
    color: COLOR.text,
    textDecoration: 'none',
    fontSize: FONT.size.md,
    fontWeight: FONT.weight.medium,
    padding: '8px 4px',
    transition: 'color .15s',
  };

  const ctaPrimaryStyle = {
    background: theme?.primary || COLOR.blue,
    color: theme?.ctaTextColor || '#fff',
    padding: '10px 18px',
    borderRadius: RADIUS.pill,
    fontSize: FONT.size.md,
    fontWeight: FONT.weight.semi,
    textDecoration: 'none',
    border: 'none',
    cursor: 'pointer',
    transition: 'transform .12s, box-shadow .12s',
    boxShadow: ELEVATION.sm,
    display: 'inline-block',
  };

  const ctaGhostStyle = {
    background: 'transparent',
    color: COLOR.text,
    padding: '10px 18px',
    borderRadius: RADIUS.pill,
    fontSize: FONT.size.md,
    fontWeight: FONT.weight.semi,
    textDecoration: 'none',
    border: `1px solid ${COLOR.grayBorder}`,
    cursor: 'pointer',
    display: 'inline-block',
  };

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: scrolled ? 'rgba(255,255,255,0.92)' : '#fff',
        backdropFilter: scrolled ? 'saturate(180%) blur(8px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'saturate(180%) blur(8px)' : 'none',
        borderBottom: `1px solid ${scrolled ? COLOR.grayBorder : 'transparent'}`,
        transition: 'background .15s, border-color .15s',
      }}
    >
      <div
        className="hhms-marketing-navbar-inner"
        style={{
          maxWidth: '100%',
          margin: '0 auto',
          padding: '14px 56px',
          display: 'flex',
          alignItems: 'center',
          gap: 24,
        }}
      >
        {/* Brand */}
        <Link to="/home" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          {brand.logoUrl
            ? <img src={brand.logoUrl} alt={brand.name} style={{ height: 40 }} />
            : <BenevitaLogo size={40} variant="full" />}
        </Link>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Desktop nav */}
        <nav
          className="hhms-marketing-desktop-nav"
          style={{ display: 'flex', gap: 28, alignItems: 'center' }}
        >
          {(nav?.links || []).map((l) => (
            <NavAnchor key={l.label} link={l} style={linkStyle} />
          ))}
        </nav>

        <div
          className="hhms-marketing-desktop-cta"
          style={{ display: 'flex', gap: 10, alignItems: 'center' }}
        >
          {nav?.ctaSecondary && <NavAnchor link={nav.ctaSecondary} style={ctaGhostStyle} />}
          {nav?.ctaPrimary   && <NavAnchor link={nav.ctaPrimary}   style={ctaPrimaryStyle} />}
        </div>

        {/* Mobile hamburger */}
        <button
          aria-label={open
            ? (ui.navBurgerCloseLabel || 'Close menu')
            : (ui.navBurgerOpenLabel  || 'Open menu')}
          onClick={() => setOpen((v) => !v)}
          className="hhms-marketing-burger"
          style={{
            display: 'none',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 8,
            fontSize: 22,
            color: COLOR.text,
          }}
        >
          {open ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div
          className="hhms-marketing-mobile-menu"
          style={{
            display: 'none',
            borderTop: `1px solid ${COLOR.grayBorder}`,
            padding: '16px 24px 24px',
            background: '#fff',
          }}
        >
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {(nav?.links || []).map((l) => (
              <NavAnchor
                key={l.label}
                link={l}
                onClick={() => setOpen(false)}
                style={{ ...linkStyle, fontSize: 16 }}
              />
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {nav?.ctaSecondary && <NavAnchor link={nav.ctaSecondary} style={ctaGhostStyle} />}
              {nav?.ctaPrimary   && <NavAnchor link={nav.ctaPrimary}   style={ctaPrimaryStyle} />}
            </div>
          </nav>
        </div>
      )}

      {/* Local responsive CSS — toggles desktop vs mobile rows. */}
      <style>{`
        @media (max-width: 820px) {
          .hhms-marketing-desktop-nav { display: none !important; }
          .hhms-marketing-desktop-cta { display: none !important; }
          .hhms-marketing-burger { display: inline-flex !important; align-items: center; justify-content: center; }
          .hhms-marketing-mobile-menu { display: block !important; }
        }
        @media (max-width: 640px) {
          .hhms-marketing-navbar-inner { padding-left: 20px !important; padding-right: 20px !important; }
        }
      `}</style>
    </header>
  );
}
